import { ReactElement } from "react";
import { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { MainLayout } from "~/components/layouts/MainLayout";
import { StandardServicePage } from "~/components/service-pages/StandardServicePage";
import { FireDetectionInspectionContent as content } from "~/content/przeglad-systemow-wykrywania-pozaru/content.pl";
import type { City } from "~/lib/cities";
import { getCitiesWithServiceOffers } from "~/lib/firms.server";
import { getRecentPostsForService, type PostMeta } from "~/lib/mdx";
import { getServiceBySlug } from "~/lib/services";
import { NextPageWithLayout } from "~/pages/_app";
import smokeAlarmInspectionImage from "~/src/assets/images/smoke_4.jpg";
import smokeAlarmDetailImage from "~/src/assets/images/smoke-alarm-check.webp";
import smokeServiceImage from "~/src/assets/images/services-details-smoke.webp";

type Props = {
  recentPosts: PostMeta[];
  availableCities: City[];
};

const serviceSlug = "przeglad-systemow-wykrywania-pozaru";
const service = getServiceBySlug(serviceSlug);

if (!service) {
  throw new Error(`Service not found: ${serviceSlug}`);
}

const pageUrl = `${(
  process.env.NEXT_PUBLIC_BASE_URL ?? "https://inspektomat.pl"
).replace(/\/+$/, "")}${service.href}`;

const sectionImages = [
  {
    src: smokeAlarmInspectionImage,
    alt: "Kontrola elementów systemu wykrywania pożaru w obiekcie",
  },
  {
    src: smokeAlarmDetailImage,
    alt: "Czujniki dymu i czujniki ciepła podczas przeglądu",
  },
  {
    src: smokeServiceImage,
    alt: "Dokumentacja i protokół z przeglądu systemów wykrywania pożaru",
  },
] as const;

const FireDetectionInspectionPage: NextPageWithLayout<Props> = ({
  recentPosts,
  availableCities,
}) => {
  return (
    <StandardServicePage
      serviceSlug={serviceSlug}
      content={content}
      sectionImages={sectionImages}
      recentPosts={recentPosts}
      availableCities={availableCities}
    />
  );
};

FireDetectionInspectionPage.getLayout = function GetLayout(
  page: ReactElement
) {
  return (
    <MainLayout>
      <NextSeo
        title={content.seo.title}
        description={content.seo.description}
        openGraph={{
          title: content.seo.title,
          description: content.seo.description,
          url: pageUrl,
        }}
      />
      {page}
    </MainLayout>
  );
};

export default FireDetectionInspectionPage;

export const getStaticProps: GetStaticProps<Props> = async () => {
  return {
    props: {
      recentPosts: getRecentPostsForService(serviceSlug),
      availableCities: getCitiesWithServiceOffers(serviceSlug),
    },
  };
};
